import Phaser from 'phaser';
import { Card, isJoker } from '../utils/CardUtils';
import { CARD_WIDTH, CARD_HEIGHT } from '../utils/Constants';

export class CardSprite extends Phaser.GameObjects.Image {
  public card: Card;
  public faceUp: boolean;
  public selected = false;
  private originalY = 0;
  private shaking = false;

  constructor(scene: Phaser.Scene, x: number, y: number, card: Card, faceUp = true) {
    const textureKey = faceUp ? CardSprite.getTextureKey(card) : 'card_back_blue';
    super(scene, x, y, textureKey);

    this.card = card;
    this.faceUp = faceUp;
    this.originalY = y;

    scene.add.existing(this);
    this.setInteractive({ useHandCursor: true });

    this.setDisplaySize(CARD_WIDTH, CARD_HEIGHT);
  }

  static getTextureKey(card: Card): string {
    if (isJoker(card)) {
      return `card_joker_${card.deckIndex}`;
    }
    return `card_${card.suit}_${card.rank}`;
  }

  setFaceUp(faceUp: boolean): void {
    this.faceUp = faceUp;
    const key = faceUp ? CardSprite.getTextureKey(this.card) : 'card_back_blue';
    this.setTexture(key);
  }

  enableHover(): void {
    this.on('pointerover', () => {
      if (!this.shaking && !this.selected) {
        this.originalY = this.y;
        this.scene.tweens.add({
          targets: this,
          y: this.y - 15,
          scaleX: 1.1,
          scaleY: 1.1,
          duration: 150,
          ease: 'Back.easeOut',
        });
        this.setTint(0xffffcc);
      }
    });

    this.on('pointerout', () => {
      if (this.selected) return; // Don't reset if selected
      this.scene.tweens.add({
        targets: this,
        y: this.originalY,
        scaleX: 1,
        scaleY: 1,
        duration: 150,
        ease: 'Back.easeOut',
      });
      this.clearTint();
    });
  }

  shake(): void {
    if (this.shaking) return;
    this.shaking = true;
    this.scene.tweens.add({
      targets: this,
      x: this.x + 3,
      duration: 50,
      yoyo: true,
      repeat: 3,
      onComplete: () => { this.shaking = false; },
    });
  }

  animateTo(x: number, y: number, duration = 300, delay = 0): Promise<void> {
    return new Promise(resolve => {
      this.scene.tweens.add({
        targets: this,
        x,
        y,
        duration,
        delay,
        ease: 'Power2',
        onComplete: () => {
          this.originalY = y;
          resolve();
        },
      });
    });
  }

  /**
   * Animate position while flipping the card at the midpoint.
   * The card scales horizontally to 0, swaps texture, then scales back to 1.
   * @param targetCard - If provided, update card data and show this card's face after flip
   */
  animateToWithFlip(x: number, y: number, targetCard?: Card, duration = 500): Promise<void> {
    return new Promise(resolve => {
      const half = duration / 2;

      // Position tween over full duration
      this.scene.tweens.add({
        targets: this,
        x,
        y,
        duration,
        ease: 'Power2',
      });

      // First half: scale X to 0
      this.scene.tweens.add({
        targets: this,
        scaleX: 0,
        duration: half,
        ease: 'Power2',
        onComplete: () => {
          // Swap texture at midpoint
          if (targetCard) {
            this.card = targetCard;
          }
          this.faceUp = !this.faceUp;
          const key = this.faceUp ? CardSprite.getTextureKey(this.card) : 'card_back_blue';
          this.setTexture(key);
          this.setDisplaySize(CARD_WIDTH, CARD_HEIGHT);

          // Second half: scale X back to 1
          this.scene.tweens.add({
            targets: this,
            scaleX: 1,
            duration: half,
            ease: 'Power2',
            onComplete: () => {
              this.originalY = y;
              resolve();
            },
          });
        },
      });
    });
  }
}
